import { ICustomerDetailsCustomerProfile } from "@/api_framework/api_modals/Users";
import RBACFlag from "@/app/components/core/RBACFlag";
import ClipboardComponent from "@/app/components/ui/Clipboard";
import { ChatBubbleLeftRightIcon, ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import { BsMic } from "react-icons/bs";
import { FaUserPlus } from "react-icons/fa6";
import { HiOutlineLink } from "react-icons/hi";
import { IoArrowForward } from "react-icons/io5";
import { PiCalculatorThin } from "react-icons/pi";

// Every action shares one identical recipe — same height, border, radius,
// color, weight and hover — so the whole bar reads as a uniform set.
// Shared base — same height, radius, weight, shadow and focus ring so the
// whole bar reads as one uniform set. Color variants below tint the border,
// background and text while keeping text dark and backgrounds light.
const actionBtnBase =
  "inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg border px-3 text-theme-xs font-semibold shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1";

const actionBtn = `${actionBtnBase} border-slate-200 bg-slate-50 text-slate-800 hover:bg-slate-100 hover:border-slate-300 focus-visible:ring-slate-300`;
const blueBtn = `${actionBtnBase} border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 hover:border-blue-300 focus-visible:ring-blue-300`;
const greenBtn = `${actionBtnBase} border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 hover:border-emerald-300 focus-visible:ring-emerald-300`;
const violetBtn = `${actionBtnBase} border-violet-200 bg-violet-50 text-violet-800 hover:bg-violet-100 hover:border-violet-300 focus-visible:ring-violet-300`;
const amberBtn = `${actionBtnBase} border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 hover:border-amber-300 focus-visible:ring-amber-300`;
// Crisp blue outline over a soft blue highlight background — a gentle highlight
// with clean text/icon, distinct from the muted actions without a muddy fill.
const primaryBtn = `${actionBtnBase} border-blue-600 bg-blue-50 text-blue-700 hover:bg-blue-100 focus-visible:ring-blue-300`;

const iconClass = "h-4 w-4 shrink-0";

const CustomerActionToolbar: React.FC<{
  customerDetails: ICustomerDetailsCustomerProfile;
  customerId: string;
  hidePetsSection?: boolean;
  onCallClick: () => void;
  onEmiClick: () => void;
  onPersonaClick: () => void;
  onSmartQCDeeplinkClick: () => void;
  onOpenControlPanelClick: () => void;
  canUseVoiceToEMR: boolean;
  voiceToEMRLoading: boolean;
  voiceToEMRDisabled: boolean;
  voiceToEMRError: string | null;
  onVoiceToEMRClick: () => void;
}> = ({
  customerDetails,
  customerId,
  hidePetsSection = false,
  onCallClick,
  onEmiClick,
  onPersonaClick,
  onSmartQCDeeplinkClick,
  onOpenControlPanelClick,
  canUseVoiceToEMR,
  voiceToEMRLoading,
  voiceToEMRDisabled,
  voiceToEMRError,
  onVoiceToEMRClick,
}) => {
    return (
      <div className="mb-2 w-full sm:mb-0 sm:w-auto">
        <div className="flex flex-wrap items-center gap-1.5">
          <ClipboardComponent
            className={actionBtn}
            tooltipText={`Copy ${customerDetails?.name}`}
            text={`${customerId}`}
            copyId={`copy-user-id:${customerId}`}
            render={
              <span className="inline-flex items-center gap-1.5">
                <ClipboardDocumentIcon className={iconClass} />
                Copy ID
              </span>
            }
          />
          {!hidePetsSection && customerDetails?.is_whatsapp_mobile && (
            <span className={greenBtn}>
              <ChatBubbleLeftRightIcon className={iconClass} />
              WhatsApp
            </span>
          )}



          {canUseVoiceToEMR && (
            <div className="hidden items-center sm:flex">
              <button
                type="button"
                disabled={voiceToEMRDisabled}
                onClick={onVoiceToEMRClick}
                title="Start a voice-recorded EMR"
                className={`${primaryBtn} disabled:cursor-not-allowed disabled:opacity-60`}
              >
                <BsMic className={iconClass} />
                {voiceToEMRLoading ? "Starting..." : "Record EMR"}
              </button>
              {voiceToEMRError && (
                <p className="ml-2 max-w-[220px] self-center text-[11px] leading-snug text-red-600">{voiceToEMRError}</p>
              )}
            </div>
          )}

          <RBACFlag actionId="MANUAL_CONTROL_PANEL_BUTTON">
            <button
              type="button"
              onClick={onOpenControlPanelClick}
              title="Open the manual control panel"
              className={`${blueBtn} group`}
            >
              <span>Control Panel</span>
              <IoArrowForward className={`${iconClass} transition-transform duration-150 group-hover:translate-x-0.5`} />
            </button>
          </RBACFlag>
          <RBACFlag actionId="SMART_QC_DEEPLINK_CTA">
            <button
              type="button"
              onClick={onSmartQCDeeplinkClick}
              title="Generate a Smart QC deeplink for this customer"
              className={violetBtn}
            >
              <HiOutlineLink className={iconClass} />
              QC Deeplink
            </button>
          </RBACFlag>
          <RBACFlag actionId="ADD_USER_PERSONA_CTA">
            <button
              type="button"
              onClick={onPersonaClick}
              title="Add a persona for this customer"
              className={`${violetBtn} ml-auto sm:ml-0`}
            >
              <FaUserPlus className={iconClass} />
            </button>
          </RBACFlag>
          {!hidePetsSection && (
            <button type="button" onClick={onCallClick} title="Call this customer" className={greenBtn}>
              <img
                src="https://vetic-img.s3.ap-south-1.amazonaws.com/Call_Icon.png"
                alt=""
                aria-hidden="true"
                className={`${iconClass} object-cover`}
              />
            </button>
          )}
          <RBACFlag actionId="EMI_CALCULATOR_CTA">
            <button type="button" onClick={onEmiClick} title="Open the EMI calculator" className={amberBtn}>
              <PiCalculatorThin className={iconClass} />
            </button>
          </RBACFlag>
        </div>
      </div>
    );
  };

export default CustomerActionToolbar;
